/**
 * The Board — the pipeline, on the device it is actually held in.
 *
 * ── Why this is not a Kanban board on a phone ───────────────────────────────
 *
 * §11 of the source specification asks for cards and a Kanban board, and on a
 * 390px screen a Kanban board is a lie: five columns is 78 pixels each, which
 * fits a card that shows nothing, and horizontal drag inside a horizontally
 * scrolling strip is a gesture that fights itself. §10.3 of the architecture
 * decided what the mobile pipeline view actually is — **a lane chip rail and a
 * list** — and that is what this is. The five drag-and-drop columns appear at
 * ≥1024px, where a column is 190px and a mouse can drag.
 *
 * So the same screen is two layouts, and neither is a degraded version of the
 * other. On a phone a stage is changed by opening the opportunity, where the
 * reason picker and the evidence fields already live. On a desktop it is
 * changed by dragging, and the drop runs the same checks first.
 *
 * ── Five lanes, not seventeen stages ────────────────────────────────────────
 *
 * `LANE_OF` groups the seventeen stages into five lanes by PURPOSE, in
 * TypeScript rather than in a column, so the grouping can change without a
 * migration. Seventeen columns is not a board on any screen; five is.
 *
 * ── A drop is checked before it is sent ─────────────────────────────────────
 *
 * `canTransition` is pure and lives in `shared/`, so this screen runs the very
 * function the handler runs and refuses locally for the same reason and with
 * the same code. CLAUDE.md rule 14: the version of the "hide the button" bug
 * that survives a fix is the one where the button is hidden and the endpoint
 * still accepts the request — here neither happens, because there is one rule.
 *
 * Dropping into DECIDING enters `finalist`, which §M requires a reason for, so
 * the drop opens the picker rather than silently doing nothing. Dropping into
 * LANDING enters `purchased`, which needs a price and a date; that is refused
 * here with the sentence that says where to record them, because inventing a
 * purchase form on a board is how a purchase gets recorded without a receipt.
 *
 * ── The counts are honest about their cap ───────────────────────────────────
 *
 * Each lane is one `GET /api/feed?lane=…` page. A lane with more rows than the
 * page holds shows `30+`, never a made-up total: there is no count endpoint for
 * a lane, and a number this screen invented would be the one number on it
 * nobody could check.
 *
 * ── §11, §10.3 ──────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { get } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { navigate, useRoute } from '../lib/route'
import {
  LANES,
  canTransition,
  laneLabel,
  requiresReason,
  stageColumns,
  stageLabel,
  type Disposition,
  type Lane,
  type ReasonKind,
  type Stage,
} from '../lib/domain'
import {
  MiniCard,
  reasonText,
  reasonsOf,
  transitionOpportunity,
  type FeedCard,
  type FeedPage,
  type ReasonRow,
} from '../components/OpportunityCard'
import { useMinWidth } from './Dashboard'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  loading: 'Loading…',
  failed: 'The board could not load.',
  retry: 'Try again',
  emptyLane: 'Nothing in this lane.',
  more: 'Load more',
  dropHere: 'Move here',
  moved: 'Moved.',
  reasonTitle: 'Why is this a finalist?',
  reasonWhy: 'The reason is what the comparison screen and the learned preferences are built on, so it is required rather than encouraged.',
  confirm: 'Move it',
  cancel: 'Cancel',
  working: 'Working…',

  r_illegal: 'That move is not allowed from where this is now.',
  r_reason_required: 'That move needs a reason.',
  r_evidence_required: 'Record the purchase price and date on the opportunity itself — a board cannot take a receipt.',
  r_conflict: 'Somebody else moved this while you were looking at it.',
  r_not_found: 'That opportunity is gone.',
  r_network: 'No connection — nothing was changed.',

  hintPhone: 'Open one to move it. Dragging needs a wider screen.',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  loading: 'Cargando…',
  failed: 'No se pudo cargar el tablero.',
  retry: 'Reintentar',
  emptyLane: 'Nada en este carril.',
  more: 'Ver más',
  dropHere: 'Mover aquí',
  moved: 'Movido.',
  reasonTitle: '¿Por qué es finalista?',
  reasonWhy: 'El motivo es la base de la pantalla de comparación y de lo que el sistema aprende, así que es obligatorio, no opcional.',
  confirm: 'Moverlo',
  cancel: 'Cancelar',
  working: 'Trabajando…',

  r_illegal: 'Ese movimiento no se permite desde donde está ahora.',
  r_reason_required: 'Ese movimiento necesita un motivo.',
  r_evidence_required: 'Anota el precio y la fecha de compra en la oportunidad misma — un tablero no puede recibir un recibo.',
  r_conflict: 'Alguien lo movió mientras lo mirabas.',
  r_not_found: 'Esa oportunidad ya no está.',
  r_network: 'Sin conexión — no se cambió nada.',

  hintPhone: 'Abre una para moverla. Arrastrar necesita una pantalla más ancha.',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const PER_LANE = 30

/* ────────────────────────────────────────────────────────────────────────────
 * Lane data
 * ────────────────────────────────────────────────────────────────────────── */

interface LaneState {
  cards: FeedCard[]
  cursor: string | null
  loading: boolean
  failed: boolean
}

const EMPTY_LANE: LaneState = { cards: [], cursor: null, loading: true, failed: false }

type LaneMap = Record<Lane, LaneState>

const blankLanes = (): LaneMap =>
  LANES.reduce((acc, lane) => {
    acc[lane] = { ...EMPTY_LANE }
    return acc
  }, {} as LaneMap)

async function fetchLane(lane: Lane, cursor: string | null): Promise<FeedPage> {
  const params = new URLSearchParams()
  params.set('lane', lane)
  params.set('disposition', 'open')
  params.set('sort', 'recent')
  params.set('limit', String(PER_LANE))
  if (cursor) params.set('cursor', cursor)
  const body = await get<{ feed: FeedPage }>(`/api/feed?${params.toString()}`)
  return body.feed
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function Board() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey): string => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()
  const wide = useMinWidth(1024)

  const columns = useMemo(() => stageColumns(locale), [locale])

  const chosen = route.query.get('lane')
  const selected: Lane = (LANES as readonly string[]).includes(chosen ?? '') ? (chosen as Lane) : 'triage'

  const [lanes, setLanes] = useState<LaneMap>(blankLanes)
  const [snack, setSnack] = useState<string | null>(null)
  const [over, setOver] = useState<Lane | null>(null)
  const [prompt, setPrompt] = useState<{ card: FeedCard; stage: Stage; kind: ReasonKind } | null>(null)
  const dragging = useRef<string | null>(null)

  const say = useCallback((message: string) => {
    setSnack(message)
    window.setTimeout(() => setSnack((held) => (held === message ? null : held)), 4000)
  }, [])

  const load = useCallback(async (lane: Lane) => {
    setLanes((held) => ({ ...held, [lane]: { ...held[lane], loading: true, failed: false } }))
    try {
      const page = await fetchLane(lane, null)
      setLanes((held) => ({
        ...held,
        [lane]: { cards: page.cards, cursor: page.cursor, loading: false, failed: false },
      }))
    } catch {
      setLanes((held) => ({ ...held, [lane]: { ...held[lane], loading: false, failed: true } }))
    }
  }, [])

  /*
   * Every lane, on both layouts. The phone shows one at a time, but a chip rail
   * whose counts arrive as you tap is a rail that is never right — and the whole
   * point of the rail is being able to see where the work has piled up without
   * opening anything.
   */
  useEffect(() => {
    for (const lane of LANES) void load(lane)
  }, [load])

  const loadMore = useCallback(
    async (lane: Lane) => {
      const state = lanes[lane]
      if (!state.cursor) return
      try {
        const page = await fetchLane(lane, state.cursor)
        setLanes((held) => {
          const known = new Set(held[lane].cards.map((card) => card.id))
          return {
            ...held,
            [lane]: {
              ...held[lane],
              cards: [...held[lane].cards, ...page.cards.filter((card) => !known.has(card.id))],
              cursor: page.cursor,
            },
          }
        })
      } catch {
        say(t('r_network'))
      }
    },
    [lanes, say, t],
  )

  /* ── Moving ──────────────────────────────────────────────────────────────── */

  const refusalText = useCallback(
    (code: string | undefined, fallback: string | undefined): string => {
      const key = `r_${code ?? 'network'}` as CopyKey
      if (Object.prototype.hasOwnProperty.call(EN, key)) return t(key)
      return fallback ?? t('r_network')
    },
    [t],
  )

  const commit = useCallback(
    async (card: FeedCard, stage: Stage, reasonCode?: string) => {
      const result = await transitionOpportunity(card.id, {
        stage,
        ...(reasonCode ? { reasonCode } : {}),
      })
      if (!result.ok) {
        say(refusalText(result.code, result.message))
        return false
      }
      say(t('moved'))
      /* Both ends, because the row left one lane and arrived in another and a
         board that only refreshes the destination shows it in two places. */
      const from = card.lane as Lane | null
      const to = (columns.find((column) => column.stages.includes(stage))?.lane ?? null) as Lane | null
      if (from) void load(from)
      if (to && to !== from) void load(to)
      return true
    },
    [columns, load, refusalText, say, t],
  )

  const drop = useCallback(
    (lane: Lane) => {
      setOver(null)
      const id = dragging.current
      dragging.current = null
      if (!id) return

      const card = LANES.flatMap((one) => lanes[one].cards).find((one) => one.id === id)
      if (!card) return

      const column = columns.find((one) => one.lane === lane)
      const stage = column?.stages[0]
      if (!stage) return
      if (card.stage === stage) return

      const state = {
        stage: card.stage as Stage,
        disposition: card.disposition as Disposition,
        isFinalist: card.isFinalist,
      }
      const outcome = canTransition(state, { stage })
      if (!outcome.ok) {
        /* `reason_required` is not a refusal to the person — it is a question.
           Everything else is a refusal, and says which one. */
        const kind = requiresReason(state, { stage })
        if (outcome.code === 'reason_required' && kind) {
          setPrompt({ card, stage, kind })
          return
        }
        say(refusalText(outcome.code, outcome.message))
        return
      }

      void commit(card, stage)
    },
    [columns, commit, lanes, refusalText, say],
  )

  /* ── Render ──────────────────────────────────────────────────────────────── */

  const laneCount = (lane: Lane): string => {
    const state = lanes[lane]
    if (state.loading && state.cards.length === 0) return '·'
    return state.cursor ? `${state.cards.length}+` : String(state.cards.length)
  }

  return (
    <div>
      {wide ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
            gap: 10,
            alignItems: 'start',
          }}
        >
          {columns.map((column) => (
            <section
              key={column.lane}
              onDragOver={(event) => {
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setOver(column.lane)
              }}
              onDragLeave={() => setOver((held) => (held === column.lane ? null : held))}
              onDrop={(event) => {
                event.preventDefault()
                dragging.current = event.dataTransfer.getData('text/plain') || dragging.current
                drop(column.lane)
              }}
              style={{
                borderRadius: 12,
                border: `1px solid ${over === column.lane ? 'var(--brand)' : 'var(--line)'}`,
                background: over === column.lane ? 'var(--brand-soft)' : 'transparent',
                padding: 8,
                minHeight: 160,
              }}
            >
              <h2
                style={{
                  margin: '0 0 8px',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                {column.label}
                <span style={{ color: 'var(--ink)' }}>{laneCount(column.lane)}</span>
              </h2>

              <LaneList
                state={lanes[column.lane]}
                locale={locale}
                t={t}
                draggable
                onDragStart={(id) => {
                  dragging.current = id
                }}
                onMore={() => void loadMore(column.lane)}
                onRetry={() => void load(column.lane)}
              />
            </section>
          ))}
        </div>
      ) : (
        <>
          <div
            role="tablist"
            aria-label="lanes"
            style={{
              display: 'flex',
              gap: 6,
              overflowX: 'auto',
              margin: '0 -16px 12px',
              padding: '0 16px 4px',
              scrollbarWidth: 'none',
            }}
          >
            {columns.map((column) => {
              const on = column.lane === selected
              return (
                <button
                  key={column.lane}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => navigate(column.lane === 'triage' ? '/board' : `/board?lane=${column.lane}`)}
                  style={{
                    flex: '0 0 auto',
                    minHeight: 38,
                    padding: '0 13px',
                    borderRadius: 999,
                    border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                    background: on ? 'var(--ink)' : 'var(--surface)',
                    color: on ? 'var(--bg)' : 'var(--muted)',
                    font: 'inherit',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {laneLabel(column.lane, locale)}
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      opacity: 0.85,
                    }}
                  >
                    {laneCount(column.lane)}
                  </span>
                </button>
              )
            })}
          </div>

          <StageGroups
            state={lanes[selected]}
            stages={columns.find((column) => column.lane === selected)?.stages ?? []}
            locale={locale}
            t={t}
            onMore={() => void loadMore(selected)}
            onRetry={() => void load(selected)}
          />

          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 14, textAlign: 'center' }}>
            {t('hintPhone')}
          </p>
        </>
      )}

      {prompt && (
        <ReasonPrompt
          card={prompt.card}
          kind={prompt.kind}
          locale={locale}
          t={t}
          onClose={() => setPrompt(null)}
          onChoose={async (code) => {
            const target = prompt
            setPrompt(null)
            await commit(target.card, target.stage, code)
          }}
        />
      )}

      {snack && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 'calc(var(--tabbar-h) + 12px)',
            zIndex: 70,
            maxWidth: 520,
            margin: '0 auto',
            padding: '11px 14px',
            borderRadius: 12,
            background: 'var(--ink)',
            color: 'var(--bg)',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: 'var(--elev-2)',
          }}
        >
          {snack}
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * A lane, flat — the desktop column
 * ────────────────────────────────────────────────────────────────────────── */

function LaneList({
  state,
  locale,
  t,
  draggable,
  onDragStart,
  onMore,
  onRetry,
}: {
  state: LaneState
  locale: Locale
  t: (key: CopyKey) => string
  draggable?: boolean
  onDragStart?: (id: string) => void
  onMore: () => void
  onRetry: () => void
}) {
  if (state.loading && state.cards.length === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t('loading')}</p>
  }
  if (state.failed && state.cards.length === 0) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
        {t('failed')}{' '}
        <button
          type="button"
          onClick={onRetry}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
            color: 'var(--brand-strong)',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('retry')}
        </button>
      </p>
    )
  }
  if (state.cards.length === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t('emptyLane')}</p>
  }

  return (
    <>
      {state.cards.map((card) => (
        <MiniCard
          key={card.id}
          card={card}
          locale={locale}
          draggable={draggable}
          onDragStart={onDragStart}
        />
      ))}
      {state.cursor && (
        <button
          type="button"
          onClick={onMore}
          style={{
            width: '100%',
            minHeight: 40,
            borderRadius: 10,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink)',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('more')}
        </button>
      )}
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * A lane, grouped by stage — the phone list
 *
 * The lane is the grouping that fits a screen; the STAGE is still the thing
 * that changed, so it stays visible as a subheading. A flat lane list would
 * hide the difference between "contacted" and "still waiting", which is the
 * difference that decides what to do today.
 * ────────────────────────────────────────────────────────────────────────── */

function StageGroups({
  state,
  stages,
  locale,
  t,
  onMore,
  onRetry,
}: {
  state: LaneState
  stages: readonly Stage[]
  locale: Locale
  t: (key: CopyKey) => string
  onMore: () => void
  onRetry: () => void
}) {
  if (state.loading && state.cards.length === 0) {
    return <p style={{ color: 'var(--muted)' }}>{t('loading')}</p>
  }
  if (state.failed && state.cards.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '28px 8px' }}>
        <p style={{ fontWeight: 700, margin: 0 }}>{t('failed')}</p>
        <button
          type="button"
          onClick={onRetry}
          style={{
            marginTop: 12,
            minHeight: 46,
            padding: '0 18px',
            borderRadius: 10,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink)',
            font: 'inherit',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('retry')}
        </button>
      </div>
    )
  }
  if (state.cards.length === 0) {
    return (
      <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '28px 8px' }}>{t('emptyLane')}</p>
    )
  }

  return (
    <>
      {stages.map((stage) => {
        const rows = state.cards.filter((card) => card.stage === stage)
        if (rows.length === 0) return null
        return (
          <section key={stage} style={{ marginBottom: 14 }}>
            <h2
              style={{
                margin: '0 0 6px',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {stageLabel(stage, locale)}
              <span>{rows.length}</span>
            </h2>
            {rows.map((card) => (
              <MiniCard key={card.id} card={card} locale={locale} />
            ))}
          </section>
        )
      })}

      {state.cursor && (
        <button
          type="button"
          onClick={onMore}
          style={{
            width: '100%',
            minHeight: 48,
            borderRadius: 10,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink)',
            font: 'inherit',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('more')}
        </button>
      )}
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The reason picker
 *
 * `decision_reasons` is the authority — admin-editable, bilingual, and it
 * carries `active` — so the sheet renders what the table returns and nothing
 * compiled in. With no signal the list comes back empty and the sheet says so
 * by having nothing to choose, which is the truth: the write would be refused.
 * ────────────────────────────────────────────────────────────────────────── */

function ReasonPrompt({
  card,
  kind,
  locale,
  t,
  onClose,
  onChoose,
}: {
  card: FeedCard
  kind: ReasonKind
  locale: Locale
  t: (key: CopyKey) => string
  onClose: () => void
  onChoose: (code: string) => Promise<void>
}) {
  const [rows, setRows] = useState<ReasonRow[]>([])
  const [chosen, setChosen] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    void reasonsOf(kind).then((found) => {
      if (live) setRows(found)
    })
    return () => {
      live = false
    }
  }, [kind])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('reasonTitle')}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(8, 9, 12, 0.5)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '82vh',
          overflowY: 'auto',
          background: 'var(--bg)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          border: '1px solid var(--line)',
          padding: '16px 14px calc(16px + env(safe-area-inset-bottom))',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{t('reasonTitle')}</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted)' }}>{card.title}</p>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
          {t('reasonWhy')}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '14px 0 4px' }}>
          {rows.map((row) => {
            const on = chosen === row.code
            return (
              <button
                key={row.code}
                type="button"
                onClick={() => setChosen(row.code)}
                aria-pressed={on}
                style={{
                  minHeight: 44,
                  padding: '0 12px',
                  borderRadius: 999,
                  border: `1px solid ${on ? 'var(--brand)' : 'var(--line)'}`,
                  background: on ? 'var(--brand-soft)' : 'var(--surface)',
                  color: on ? 'var(--brand-strong)' : 'var(--ink)',
                  font: 'inherit',
                  fontSize: 14,
                  fontWeight: on ? 800 : 600,
                  cursor: 'pointer',
                }}
              >
                {reasonText(row, locale)}
              </button>
            )
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: '1 1 0',
              minHeight: 46,
              borderRadius: 10,
              border: '1px solid var(--line)',
              background: 'var(--bg)',
              color: 'var(--ink)',
              font: 'inherit',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            disabled={!chosen || busy}
            onClick={() => {
              if (!chosen) return
              setBusy(true)
              void onChoose(chosen)
            }}
            style={{
              flex: '1.4 1 0',
              minHeight: 46,
              borderRadius: 10,
              border: `1px solid ${chosen ? 'var(--brand)' : 'var(--line)'}`,
              background: chosen ? 'var(--brand)' : 'var(--surface)',
              color: chosen ? 'var(--brand-ink)' : 'var(--muted)',
              font: 'inherit',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            {busy ? t('working') : t('confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
